import React from 'react';
import { Flex, Progress, Text, Box, useColorMode } from '@chakra-ui/react';
import { DataTableHeader } from './DataTableHeader';
import { DataTableFooter } from './DataTableFooter';
import { ThemedFlex } from '../../theme/ThemedFlex';

const DIFFICULTY_ADJUSTMENT_INTERVAL = 2016;

export const DifficultyAdjustmentTile = ({
  title,
  icon,
  subtitle,
  link,
  linkLabel,
  lastMempoolBlockData,
  da,
  blockMetrics,
  animate
}) => {
  const currentHeight = lastMempoolBlockData ? lastMempoolBlockData.height : 0;
  const diffChangePercentage = da ? `${da.difficultyChange.toFixed(3)}%` : "Loading...";

  // Use blockMetrics if available (event-driven), otherwise calculate
  const progress = blockMetrics?.difficultyProgress
    ? blockMetrics.difficultyProgress.progress
    : (() => {
        const adjustmentCount = Math.floor(currentHeight / DIFFICULTY_ADJUSTMENT_INTERVAL);
        const startHeight = adjustmentCount * DIFFICULTY_ADJUSTMENT_INTERVAL - 1;
        const endHeight = (adjustmentCount + 1) * DIFFICULTY_ADJUSTMENT_INTERVAL - 1;
        return ((currentHeight - startHeight) / (endHeight - startHeight)) * 100;
      })();

  const blocksRemaining = blockMetrics?.difficultyProgress?.blocksRemaining
    || (Math.floor((currentHeight / DIFFICULTY_ADJUSTMENT_INTERVAL) + 1) * DIFFICULTY_ADJUSTMENT_INTERVAL - currentHeight);

  const indicatorPosition = progress + '%';
  const { colorMode } = useColorMode();
  const gridColor = colorMode === 'dark' ? '#121212' : '#CCCCCC';
  const bgColor = colorMode === 'dark' ? 'orange.500' : '#0d579b';
  const schemeColor = colorMode === 'dark' ? 'orange' : 'blue';
  const indicatorColor = colorMode === 'dark' ? '#121212' : '#ffffff';

  // Add previous difficulty change
  const previousDiffChangePercentage = da ? `${da.previousRetarget.toFixed(3)}%` : "Loading...";

  return (
    <ThemedFlex
      direction="column"
      mb="12px"
      py="18px"
      px="20px"
      flex={1}
      borderRadius={3}
      className={animate ? 'gray-border-custom gray-border' : ''}
    >
      <DataTableHeader title={title} subtitle={subtitle} icon={icon} />
      <Progress
        value={progress}
        colorScheme={schemeColor}
        bg={gridColor}
        size="sm"
        mb="2"
        mt="2"
      />
      <Box position="relative">
        <Box position="absolute" left={indicatorPosition} top="-48px" transform="translateX(-50%)">
          <Box bgColor={bgColor} color={indicatorColor} py="2px" px="8px" borderRadius="md" fontWeight="bold">
            <Text fontSize="14px">{Math.floor(progress)}%</Text>
          </Box>
          <Box bgColor={bgColor} w="10px" h="10px" transform="rotate(45deg)" position="absolute" left="40%" bottom="-5px" />
        </Box>
      </Box>
      <Flex justifyContent="space-between" mt="2">
        <Flex direction="column">
          <Text fontSize={{ base: "12px", md: "14px" }} fontWeight={'bold'}>
            {blocksRemaining.toLocaleString()} blocks left
          </Text>
          <Text fontSize={{ base: "10px", md: "12px" }} color="gray">
            of {DIFFICULTY_ADJUSTMENT_INTERVAL} blocks
          </Text>
        </Flex>
        <Text fontSize={{ base: "9px", md: "13px" }} color="gray" fontWeight={'bold'} display={{ xs: "none", sm: "inline" }}>
          {blockMetrics?.difficultyProgress?.estimatedTime
            ? `est. ${new Date(Date.now() + (blockMetrics.difficultyProgress.estimatedTime * 1000)).toLocaleDateString()}`
            : `est. ${new Date(blocksRemaining * 10 * 60 * 1000 + Date.now()).toLocaleDateString()}`
          }
        </Text>
        <Flex direction="column" alignItems="flex-end">
          <Text fontSize={{ base: "12px", md: "14px" }} fontWeight={'bold'}>
            Est change {diffChangePercentage}
          </Text>
          <Text fontSize={{ base: "10px", md: "12px" }} color="gray">
            Previous: {previousDiffChangePercentage}
          </Text>
        </Flex>
      </Flex>
      <DataTableFooter link={link} linkLabel={linkLabel} />
    </ThemedFlex>
  );
};
